import { css } from "lit";
import { ImperativeElement } from "../view.ts";
import { type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { select } from "d3-selection";
import {
  type Arc,
  arc,
  curveBundle,
  type DefaultArcObject,
  type LineRadial,
  lineRadial,
} from "d3-shape";
import {
  cluster,
  type ClusterLayout,
  hierarchy as hierarchyLayout,
  type HierarchyPointNode,
} from "d3-hierarchy";

export type Node = {
  id: string;
  title: string;
  content: string;
  category?: string;
};

export type Link = {
  id: string;
  title: string;
  content: string;
  source: string;
  target: string;
  strength: number;
};

export type ToCategoryColorFn = (category: string | undefined) => string;

const defaultCategoryColor: ToCategoryColorFn = () => "var(--bg3)";

const styles = css`
  :host {
    display: block;
    width: 100cqw;
    height: 100cqh;
  }

  #radial-network {
    width: 100%;
    height: 100%;
  }

  .link {
    fill: none;
    stroke: var(--foreground);
    opacity: 0.1;
    transition: opacity var(--duration-normal) ease-in-out;
  }

  .node {
    cursor: pointer;
    transition: opacity var(--duration-normal) ease-in-out;
  }

  .node-label {
    font-size: var(--font-size-sm);
    fill: var(--foreground);
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--duration-normal) ease-in-out;
  }

  .category-label {
    font-size: var(--font-size-md);
    font-weight: bold;
    fill: var(--foreground);
    opacity: 0.7;
  }

  .category-arc {
    pointer-events: none;
  }
`;

type HierarchyNode = {
  name: string;
  category?: string;
  id?: string;
  children?: HierarchyNode[];
};

type ClusterNode = HierarchyPointNode<HierarchyNode>;

export type ActivateNodeEvent = CustomEvent<string>;

type LinkData = {
  source: ClusterNode;
  target: ClusterNode;
  link: Link;
};

@customElement("radial-force-network")
export class RadialForceNetwork extends ImperativeElement {
  static override styles = [Styles.base, styles];

  @property({ type: Array })
  accessor nodes: Node[] = [];

  @property({ type: Array })
  accessor links: Link[] = [];

  @property({ attribute: false })
  accessor categoryColor: ToCategoryColorFn = defaultCategoryColor;

  readonly #width = 720;
  readonly #height = 720;
  readonly #radius = Math.min(this.#width, this.#height) / 2 - 40;

  #arcGenerator!: Arc<unknown, DefaultArcObject>;
  #lineGenerator!: LineRadial<ClusterNode>;
  #cluster!: ClusterLayout<HierarchyNode>;

  protected override firstUpdate(
    changedProperties: PropertyValues<this>,
  ): void {
    super.firstUpdate(changedProperties);

    const root = select(this.shadowRoot as unknown as Document);

    // Create SVG
    const view = root
      .append("svg")
      .attr("id", "radial-network")
      .attr("viewBox", `0 0 ${this.#width} ${this.#height}`);

    // Create main group with transform
    const g = view
      .append("g")
      .attr("class", "main")
      .attr("transform", `translate(${this.#width / 2},${this.#height / 2})`);

    // Create group containers
    g.append("g").attr("class", "arcs");
    g.append("g").attr("class", "links");
    g.append("g").attr("class", "nodes");
    g.append("g").attr("class", "categories");

    // Create generators
    this.#arcGenerator = arc()
      .innerRadius(this.#radius - 4)
      .outerRadius(this.#radius + 4);

    this.#lineGenerator = lineRadial<ClusterNode>()
      .curve(curveBundle.beta(0.85))
      .radius((d: ClusterNode) => d.y)
      .angle((d: ClusterNode) => d.x);

    this.#cluster = cluster<HierarchyNode>().size([2 * Math.PI, this.#radius]);
  }

  protected override update(changedProperties: PropertyValues<this>): void {
    super.update(changedProperties);

    const root = select(this.shadowRoot as unknown as Document);

    const view = root.select("#radial-network");
    const g = view.select("g.main");

    // Create hierarchical data structure
    const categoryMap = new Map<string, Node[]>();
    for (const node of this.nodes) {
      const category = node.category ?? "none";
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(node);
    }

    // Build hierarchy
    const hierarchyData: HierarchyNode = {
      name: "root",
      children: Array.from(categoryMap.entries()).map(([category, nodes]) => ({
        name: category,
        category: category,
        children: nodes.map((node) => ({
          name: node.title,
          id: node.id,
          category: category,
        })),
      })),
    };

    const hierarchy = hierarchyLayout(hierarchyData);
    const clusterRoot = this.#cluster(hierarchy);

    // Get all leaf nodes (forces)
    const leafNodes = clusterRoot
      .descendants()
      .filter((d: ClusterNode) => !d.children);

    // Create node lookup by ID
    const nodeById = new Map<string, ClusterNode>();
    for (const node of leafNodes) {
      if (node.data.id) {
        nodeById.set(node.data.id, node);
      }
    }

    // Create links
    const linkDataList: LinkData[] = [];
    for (const link of this.links) {
      const sourceNode = nodeById.get(link.source);
      const targetNode = nodeById.get(link.target);
      if (sourceNode && targetNode) {
        linkDataList.push({
          source: sourceNode,
          target: targetNode,
          link,
        });
      }
    }

    // Calculate angle ranges for each category
    const categoryNodes = clusterRoot
      .descendants()
      .filter((d: ClusterNode) => d.depth === 1 && d.children);

    type CategoryArc = {
      category: string | undefined;
      startAngle: number;
      endAngle: number;
    };

    const categoryArcs: CategoryArc[] = categoryNodes.map(
      (catNode: ClusterNode) => {
        const childAngles = catNode
          .leaves()
          .map((leaf: ClusterNode) => leaf.x)
          .sort((a: number, b: number) => a - b);
        const startAngle = childAngles[0];
        const endAngle = childAngles[childAngles.length - 1];
        return {
          category: catNode.data.category,
          startAngle,
          endAngle,
        };
      },
    );

    // Draw arcs for each category
    g.select("g.arcs")
      .selectAll("path.category-arc")
      .data(categoryArcs)
      .join("path")
      .attr("class", "category-arc")
      .attr(
        "d",
        (d: CategoryArc) =>
          this.#arcGenerator({
            startAngle: d.startAngle,
            endAngle: d.endAngle,
            innerRadius: this.#radius - 4,
            outerRadius: this.#radius + 4,
          }) as string,
      )
      .style(
        "filter",
        (d: CategoryArc) =>
          `drop-shadow(0 0 16px ${this.categoryColor(d.category)})`,
      )
      .attr("fill", (d: CategoryArc) => this.categoryColor(d.category))
      .attr("opacity", 0.4);

    // Draw links with hierarchical edge bundling
    g.select("g.links")
      .selectAll(".link")
      .data(linkDataList)
      .join("path")
      .attr("class", "link")
      .attr("d", (d: LinkData) => {
        // Find common ancestor path for bundling
        const source = d.source;
        const target = d.target;

        // Get ancestors path
        const sourceAncestors = source.ancestors().reverse();
        const targetAncestors = target.ancestors().reverse();

        // Find common ancestor
        let i = 0;
        while (
          i < sourceAncestors.length &&
          i < targetAncestors.length &&
          sourceAncestors[i] === targetAncestors[i]
        ) {
          i++;
        }

        // Create path through hierarchy
        const path = [
          ...sourceAncestors.slice(i - 1).reverse(),
          ...targetAncestors.slice(i),
        ];

        return this.#lineGenerator(path as ClusterNode[]);
      })
      .attr("stroke", (d: LinkData) =>
        this.categoryColor(d.source.data.category),
      )
      .attr("stroke-width", (d: LinkData) => {
        const s = d.link.strength ** 2;
        return 0.5 + s * 3.5;
      });

    // Draw nodes
    const nodes = g
      .select("g.nodes")
      .selectAll(".node")
      .data(leafNodes)
      .join("g")
      .attr("class", "node")
      .attr("transform", (d: ClusterNode) => {
        const angle = d.x;
        const radius = d.y;
        const x = Math.cos(angle - Math.PI / 2) * radius;
        const y = Math.sin(angle - Math.PI / 2) * radius;
        return `translate(${x},${y})`;
      });

    nodes
      .selectAll("circle")
      .data((d: ClusterNode) => [d])
      .join("circle")
      .attr("r", 4)
      .attr("fill", (d: ClusterNode) => this.categoryColor(d.data.category))
      .style(
        "filter",
        (d: ClusterNode) =>
          `drop-shadow(0 0 8px ${this.categoryColor(d.data.category)})`,
      )
      .on("click", (_event: MouseEvent, d: ClusterNode) => {
        if (d.data.id) {
          this.dispatchEvent(
            new CustomEvent("activate-node", {
              detail: d.data.id,
              bubbles: true,
              composed: true,
            }),
          );
        }
      });

    nodes
      .selectAll("text")
      .data((d: ClusterNode) => [d])
      .join("text")
      .attr("class", "node-label")
      .attr("y", -10)
      .attr("text-anchor", "middle")
      .text((d: ClusterNode) => d.data.name)
      .style("font-size", "13px");

    // Draw category labels
    g.select("g.categories")
      .selectAll(".category-label")
      .data(categoryNodes)
      .join("text")
      .attr("class", "category-label")
      .attr("transform", (d: ClusterNode) => {
        const angle = d.x;
        const radius = d.y - 40; // Position slightly inward
        const x = Math.cos(angle - Math.PI / 2) * radius;
        const y = Math.sin(angle - Math.PI / 2) * radius;
        return `translate(${x},${y})`;
      })
      .attr("dy", "0.31em")
      .attr("text-anchor", "middle")
      .text((d: ClusterNode) => d.data.category ?? "None");

    // Add hover interactions
    nodes
      .on("mouseenter", (_event: MouseEvent, d: ClusterNode) => {
        const hoveredId = d.data.id;

        // Find connected node IDs by scanning connections
        const connectedIds = new Set<string>();
        if (hoveredId) {
          connectedIds.add(hoveredId);

          for (const link of this.links) {
            if (link.source === hoveredId) {
              connectedIds.add(link.target);
            }
            if (link.target === hoveredId) {
              connectedIds.add(link.source);
            }
          }
        }

        // Highlight connected elements
        g.select("g.nodes")
          .selectAll<SVGGElement, ClusterNode>(".node")
          .style("opacity", (d: ClusterNode) => {
            return d.data.id && connectedIds.has(d.data.id) ? 1 : 0.2;
          });

        // Show labels for hovered node and connected nodes
        g.select("g.nodes")
          .selectAll<SVGGElement, ClusterNode>(".node")
          .each(function (this: SVGGElement, d: ClusterNode) {
            const nodeId = d.data.id;
            select(this)
              .select(".node-label")
              .style("opacity", nodeId && connectedIds.has(nodeId) ? 1 : 0);
          });

        g.select("g.links")
          .selectAll(".link")
          .style("opacity", (d: unknown) => {
            const link = d as LinkData;
            return link.source.data.id === hoveredId ||
              link.target.data.id === hoveredId
              ? 0.8
              : 0.05;
          });
      })
      .on("mouseleave", () => {
        // Reset all elements
        g.select("g.nodes").selectAll(".node").style("opacity", 1);

        g.select("g.nodes").selectAll(".node-label").style("opacity", 0);

        g.select("g.links").selectAll(".link").style("opacity", null);
      });
  }
}
